import type { CoachingTransportDto } from '@/api/_trpc/routers/coaching-service-error'
import type { CardDetailsDto } from '@/lib/coaching/card-details-query'
import type { CardReviewDto } from '@/lib/coaching/card-review-query'
import type { ContainerRef } from '@/lib/coaching/curriculum-query'
import type { CoachingPace } from '@/lib/coaching/estimate-minutes'
import type { CurriculumPreset as FixtureCurriculumPreset } from '../../_mock-data/library'
import type { CascadeDestination, TrayCard, Week } from '../_lib/auto-place'
import type { FilterNode, LibraryItem } from '../_lib/filter-engine'
import type { SeenFilter, SeenOverlayStatus } from '../_lib/seen-filter'
import type { SortSpec } from '../_lib/sorting'

export type CurriculumPreset = FixtureCurriculumPreset

/**
 * What the client actually receives from `getCardDetails`.
 *
 * Every admin coaching procedure is wrapped in `withCoachingServiceErrors`,
 * which serializes `Date` fields to ISO strings for the trip over tRPC (see
 * `CoachingTransportDto` — the same reason `Plan`/`Browse`/`Calendar` in
 * `persisted-curriculum.tsx` are `Outputs[...]` aliases rather than the
 * server's own DTOs). `CardDetailsDto.history.coachReportedAt` and
 * `attempts[].at` are `Date` server-side, so the raw DTO type is honest about
 * what the query returns, not about what lands here — using it directly for
 * this field would type-check against server-side callers and then fail at
 * the slice/component boundary the moment real data flows through it.
 */
export type CardDetailsResult = CoachingTransportDto<CardDetailsDto>

/**
 * The wire shape of one related problem (expert review B7) — derived from
 * `CardDetailsResult`, not restated from the server-side
 * `RelatedProblemDto`. The DTO happens to carry no `Date` today, so a
 * component prop typed as the raw server DTO type-checks against the
 * transport array `CardDetailsResult` actually hands it — by coincidence,
 * not by contract. `history`/`attempts` were given this same
 * transport-derived treatment (`parseProblemHistory`/`parseProblemAttempts`
 * in `_lib/history-log.ts`) because they *do* carry `Date`s. The source
 * options in the same payload follow the doctrine through `SourceOption` in
 * `_lib/source-option.ts`, the one shared derivation of that shape.
 */
export type RelatedProblemResult = CardDetailsResult['related'][number]

/**
 * What the client receives from `reviews.getForAssignment` (LG-360 §3) —
 * the same transport treatment `CardDetailsResult` gets above, and for the
 * same reason: `submittedAt`, `reviewedAt` and every comment's `createdAt`
 * are `Date`s server-side and ISO strings by the time they land here.
 * `_lib/card-review.ts` is the one place they turn back into `Date`s.
 */
export type CardReviewResult = CoachingTransportDto<CardReviewDto>

/** The review itself, derived from the transport shape. */
export type CardReviewSummaryResult = NonNullable<CardReviewResult['review']>

/** One comment on it, likewise derived rather than restated. */
export type CardReviewCommentResult =
  CardReviewSummaryResult['comments'][number]

/**
 * Everything the modal can know about the open card's review, as one tagged
 * value that always names the card it is about.
 *
 * A tag rather than a data field plus companion flags: with `review`,
 * `pending` and `error` carried separately, "loaded a review" and "the
 * fetch failed" are representable at the same time, and the reader has to
 * infer which phase it is in by elimination. Here the phases are named and
 * exclusive, and `assignmentId` is the one thing every consumer still has
 * to check — the container holds the previous card's state for a render
 * after a new card opens, so a state that does not name the card on screen
 * means its fetch has not started yet, whatever phase it claims.
 */
export type CardReviewState = { assignmentId: string } & (
  | { phase: 'pending' }
  | { phase: 'error' }
  /** `review: null` — settled, and this card has nothing submitted. */
  | { phase: 'loaded'; review: CardReviewSummaryResult | null }
)

/**
 * The outcome of a curriculum write that reports next to the field that caused
 * it rather than through the board-level flash.
 *
 * Defined here, with the slice that produces it, rather than in the dialog that
 * happens to render it — the dialog is a leaf, and a contract that imports from
 * its own consumer inverts the layering the mock slice and the store are both
 * built against.
 */
export interface CardDetailsSaveResult {
  /** Null on success; a coach-readable sentence on failure. */
  error: string | null
}

/**
 * One filter-builder source dropdown entry — slug value + display label. A
 * different concept from `SourceOption` in `_lib/source-option.ts` (a
 * per-problem assignable source), hence the distinct name: the two shapes
 * briefly shared it, and auto-import offered both.
 */
export interface SourceFilterOption {
  value: string
  label: string
}

export type LibraryTab = 'browse' | 'topic' | 'decay' | 'sources' | 'interview'

export interface CompanyTag {
  company: string
  mode: 'include' | 'exclude'
}

export type TypeFilter = 'all' | 'coding' | 'sd' | 'behavioral'

// Shared by the mock reducer and the persisted slice so the browse tab's
// default type pill only ever changes in one place.
export const DEFAULT_TYPE_FILTER: TypeFilter = 'coding'

export interface CustomCardInput {
  title: string
  type: TrayCard['type']
  meta: string
  /**
   * The one estimate. Minutes, not hours: the coach steps this in 5s, and
   * `TrayCard.est` (hours) is derived from it at card-construction time. An
   * earlier revision carried both and let them be set independently, which
   * type-checked happily for `{ est: 3, estimatedMinutes: 30 }` — one card
   * eating 3h of day budget while persisting a half-hour assignment.
   */
  estimatedMinutes: number
  link?: string
  /** Free text the client sees under "Details" in their task dialog. */
  instructions?: string
}

// Also carries SeenOverlayStatus (see _lib/seen-filter.ts): the persisted
// slice's seen-overlay readiness/failure flags, absent in mock mode.
export interface CurriculumSlice extends SeenOverlayStatus {
  flash: string | null
  dismissFlash: () => void
  weeks: Week[]
  tray: TrayCard[]
  filters: FilterNode[]
  sorts: SortSpec[]
  typeFilter: TypeFilter
  companyTags: CompanyTag[]
  hiddenCols: string[]
  // Browse-table column order as a list of column keys; empty means the
  // default order. Keys missing from the list keep their default position
  // after the ordered ones.
  columnOrder: string[]
  seenFilter: SeenFilter
  libraryTab: LibraryTab
  placing: TrayCard | null
  activePresetId: string | null
  /**
   * `sourceId` on both add paths is the coach's session pick from the row's
   * pinned source mark (LG-362 §3 revision — the row is added wearing the
   * identity it shows); omitted/null means the default add — the card wears
   * the problem's owning source at display time.
   */
  addAuto: (item: LibraryItem, sourceId?: string | null) => void
  stageToTray: (item: LibraryItem, sourceId?: string | null) => void
  removeFromTray: (cardId: string) => void
  togglePlacing: (cardId: string) => void
  cancelPlacing: () => void
  placeAt: (weekIdx: number, dayIdx: number) => void
  placeCardAt: (cardId: string, weekIdx: number, dayIdx: number) => void
  removeFromDay: (cardId: string) => void
  moveCardTo: (cardId: string, weekIdx: number, dayIdx: number) => void
  distributeTray: () => void
  optimizeSchedule: () => void
  publishWeek: (weekId: string) => void
  unpublishWeek: (weekId: string) => void
  deleteWeek: (weekId: string) => void
  addWeek: () => void
  setBudget: (weekIdx: number, dayIdx: number, budget: number) => void
  toggleWeek: (weekId: string) => void
  toggleWeekFold?: (weekId: string) => void // persisted only; _lib/week-fold.ts
  addCustomCard: (
    weekIdx: number,
    dayIdx: number,
    card: CustomCardInput,
  ) => void
  convertToAiMock: (cardId: string) => void
  revertAiMock: (cardId: string) => void
  linkAiMockQuestion: (cardId: string, item: LibraryItem) => void
  addInterviewTier: (
    tier: 'ai' | 'peer' | 'coach',
    destination: 'auto' | 'tray',
  ) => void
  applyPreset: (id: string) => void
  setFilters: (filters: FilterNode[]) => void
  setSorts: (sorts: SortSpec[]) => void
  setTypeFilter: (filter: TypeFilter) => void
  cycleCompanyTag: (company: string) => void
  cycleSeenFilter: () => void
  toggleColumn: (column: string) => void
  setColumnOrder: (order: string[]) => void
  setLibraryTab: (tab: LibraryTab) => void
  dayUsedHours: (weekIdx: number, dayIdx: number) => number
  persisted?: boolean
  libraryItems?: LibraryItem[]
  // Learning items with an active incomplete assignment in this engagement.
  // Surfaces the same "already in the plan" state the Browse tab shows, for
  // tabs that build their own rows instead of consuming libraryItems.
  assignedLearningItemIds?: ReadonlySet<string>
  // Live study-plan names for the persisted filter builder's plan dropdown.
  planOptions?: string[]
  // Live problem sources for the persisted filter builder's source dropdown.
  sourceOptions?: SourceFilterOption[]
  // Sources-tab tree expansion (LG-312). Kept on the slice, not in the tab,
  // so the set survives library-tab switches; persisted-only (like
  // `toggleWeekFold`) because the tree requires the persisted curriculum.
  // Null = untouched, so the tab renders its default view (sources open,
  // containers collapsed) without knowing any ids up front.
  sourceTreeOpen?: ReadonlySet<string> | null
  setSourceTreeOpen?: (open: ReadonlySet<string>) => void
  /**
   * Cascade assign a container (LG-907): one card per member of its whole
   * subtree, rather than one opaque card for the container itself. Distinct
   * from `addAuto`/`stageToTray` because it takes a container, not a
   * `LibraryItem` subject, and comes back with a count instead of one card.
   *
   * Persisted-only, for the same reason `sourceTreeOpen` is: the only surface
   * that offers it is the Sources tree, which does not render on the mock
   * board at all.
   */
  assignContainer?: (
    container: ContainerRef,
    destination: CascadeDestination,
  ) => void
  clientElo?: number | null
  optimalEloBand?: number | null
  pace?: CoachingPace
  setPace?: (pace: CoachingPace) => void
  // True while a setPace write is in flight — the tray disables the select
  // and shows the optimistic choice instead of the stale server value.
  pacePending?: boolean
  // Cards with an in-flight remove (cancel) — the tray disables their trash
  // control so a double-click can't send a stale-version second cancel.
  removingIds?: string[]
  targetCompanies?: Array<{ id: string; name: string }>
  // Resolves false when the write failed (the plan has been refreshed to
  // server truth) so the stepper can drop its optimistic value; void-returning
  // implementations (tests, mocks) are treated as fire-and-forget.
  setAssignmentEstimate?: (
    cardId: string,
    estimatedMinutes: number | null,
  ) => void | Promise<boolean>
  setAssignmentTargetCompany?: (
    cardId: string,
    targetCompanyId: string | null,
  ) => void
  // The card-details modal's three writes (LG-360 §2/§3/§6). Each resolves to
  // the outcome rather than routing it through the board flash, because the
  // open dialog covers the board — see `detailWrite` in persisted-curriculum.
  saveCoachNote?: (input: {
    cardId: string
    note: string | null
  }) => Promise<CardDetailsSaveResult>
  saveUniversalNotes?: (input: {
    learningItemId: string
    notes: string | null
    expectedNotes: string | null
  }) => Promise<CardDetailsSaveResult>
  setAssignmentCompletion?: (input: {
    cardId: string
    target: 'completed' | 'active'
  }) => Promise<CardDetailsSaveResult>
  // The problem whose details the open modal wants, or null when closed (or
  // when the open card has no catalog problem behind it, e.g. a one-off
  // card). The tab pushes this in; the persisted slice owns the tRPC fetch
  // it drives — see `saveSource` and `cardDetails` below.
  setDetailsProblemId?: (problemId: string | null) => void
  cardDetails?: CardDetailsResult | null
  cardDetailsPending?: boolean
  // True when the most recent `getCardDetails` fetch for the open problem
  // failed — distinct from `cardDetails === null && !cardDetailsPending`,
  // which is also true for the one tick before the very first fetch for a
  // newly-opened card starts. Without this flag the two are indistinguishable
  // from outside the fetch effect, and a coach-facing "couldn't load" line
  // would have no honest condition to render on. Cleared at the start of
  // every new fetch (including a retry of the same problem), so a stale
  // failure never outlives the request that produced it.
  cardDetailsError?: boolean
  // The assignment whose review the open modal wants, or null when closed
  // (LG-360 §3). Separate from `setDetailsProblemId` above because a review
  // belongs to the *card*, not to the problem behind it: a system-design or
  // behavioral card carries no problem at all and can still have been
  // submitted and reviewed.
  setDetailsAssignmentId?: (assignmentId: string | null) => void
  // Where that read has got to, for the card it names — null while the
  // modal is closed. Absent entirely (not null) on a slice that does no
  // review read at all, which is how the modal knows to render no review
  // section rather than a section stuck on "Loading…".
  cardReview?: CardReviewState | null
  saveSource?: (input: {
    cardId: string
    sourceId: string | null
  }) => Promise<CardDetailsSaveResult>
  // The inline switcher on tray/schedule cards (LG-362 §2). Same write as
  // `saveSource`, but the control lives on the board, so failures report
  // through the board flash instead of resolving a result for a dialog to
  // render. Optional like every other detail write: the mock slice does not
  // implement it, and the cards fall back to a static mark without it.
  switchCardSource?: (input: {
    cardId: string
    sourceId: string | null
  }) => Promise<void>
  // The related-problem modal's writes (LG-360 §8, Task 11). Both resolve a
  // `CardDetailsSaveResult` for the same reason `saveSource` et al. do: the
  // modal renders the outcome next to the button that caused it, not through
  // the board flash. Optional for the same reason every other detail write
  // is — the mock (non-persisted) slice does not implement them, and
  // `curriculum-tab.tsx` falls back to an honest "not built yet" result
  // rather than a silent no-op when either is missing.
  stageRelatedProblem?: (input: {
    learningItemId: string
    catalogId: string
    title: string
    estimatedMinutes: number | null
    eloRating: number | null
    target: { weekIdx: number; dayIdx: number } | 'tray'
  }) => Promise<CardDetailsSaveResult>
  replaceWithRelatedProblem?: (input: {
    cardId: string
    learningItemId: string
    // Only for the "already active" message below — mirrors
    // `stageRelatedProblem`'s own pre-check so the two sibling writes give
    // the same coach-facing sentence for the same mistake.
    title: string
    weekIdx: number
    dayIdx: number
  }) => Promise<CardDetailsSaveResult>
  retryStage?: () => void
  retryStageLabel?: string | null
  stagePending?: boolean
  bulkActionsReady?: boolean
  bulkActionsReason?: string | null
  addWeekReady?: boolean
  addWeekReason?: string | null
  // True while the corpus is still filling in behind the rows already shown.
  // Not exclusive with `browseRefreshing`: a filter change is both at once.
  browseStreaming?: boolean
  // True when the drain stopped with rows still unfetched — a failed page or
  // the paging cap. The rows on screen are a prefix of the library, so the
  // count, the filters and the sorts all describe less than they claim. The
  // browse table must render this beside the count: a partial corpus that
  // looks complete is worse than a visibly broken one.
  browseIncomplete?: boolean
  // True while a persisted filter change is refetching corpus-wide; the
  // browse table dims and marks itself busy so stale rows read as updating.
  browseRefreshing?: boolean
  planHasMore?: boolean
  planLoadingMore?: boolean
  loadMorePlan?: () => void
  planHistoryHasMore?: boolean
  loadEarlierPlan?: () => void
  trayHasMore?: boolean
  trayLoadingMore?: boolean
  loadMoreTray?: () => void
}

export interface CurriculumState {
  flash: string | null
  weeks: Week[]
  tray: TrayCard[]
  filters: FilterNode[]
  sorts: SortSpec[]
  typeFilter: TypeFilter
  companyTags: CompanyTag[]
  hiddenCols: string[]
  columnOrder: string[]
  seenFilter: SeenFilter
  libraryTab: LibraryTab
  placingId: string | null
  nextId: number
}
